import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency as globalFormatCurrency } from '../../utils/currency';
import { STATIC_URL } from '../../services/api';

interface InvoiceTemplateProps {
    invoice: any;
    profile: any;
}

const InvoiceTemplate: React.FC<InvoiceTemplateProps> = ({ invoice, profile }) => {
    const { currency } = useAuth();

    // Safety fallback data
    const safeProfile = profile || {};
    const safeCustomer = invoice?.customer || {};

    // Formatting currency
    const formatCurrency = (amount: number) => {
        return globalFormatCurrency(amount, currency);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, ' / ');
    };

    return (
        <div className="invoice-container w-full max-w-4xl mx-auto bg-white text-black p-8 pb-52 text-sm relative box-border" style={{ minHeight: '297mm', height: '297mm', fontFamily: '"Arial", sans-serif', fontSize: '17px' }}>
            {/* Header / Logo */}
            <div className="flex flex-col items-center mb-8">
                {/* Auto-Entrepreneur Logo */}
                <div className="w-[288px] h-[144px] mb-2 flex items-center justify-center">
                    {safeProfile.logoUrl ? (
                        <img
                            src={`${STATIC_URL}${safeProfile.logoUrl}`}
                            alt="Company Logo"
                            className="object-contain h-full"
                        />
                    ) : (
                        <img
                            src="/ae-logo.png"
                            alt="Auto-Entrepreneur Maroc"
                            className="object-contain h-full"
                        />
                    )}
                </div>
            </div>

            {/* Date */}
            <div className="flex justify-end mb-4">
                <p className="mr-8" contentEditable={true} suppressContentEditableWarning={true}>Date : {formatDate(invoice?.date)}</p>
            </div>

            {/* Invoice Number Box */}
            <div className="flex justify-center mb-10">
                <div className="border border-black px-4 py-1 inline-block" style={{ backgroundColor: '#ececec' }}>
                    <h2 className="text-base font-bold" contentEditable={true} suppressContentEditableWarning={true}>N° Invoice-{invoice?.invoiceNumber}</h2>
                </div>
            </div>

            {/* Client Details */}
            <div className="mb-12">
                <table className="w-2/3 ml-12 border-collapse">
                    <tbody>
                        <tr>
                            <td className="w-24 text-right pr-4 pb-1 align-top text-gray-700" contentEditable={true} suppressContentEditableWarning={true}>Client :</td>
                            <td className="font-medium pb-1" contentEditable={true} suppressContentEditableWarning={true}>{safeCustomer.displayName || safeCustomer.name || ''}</td>
                        </tr>
                        <tr>
                            <td className="w-24 text-right pr-4 pb-1 align-top text-gray-700" contentEditable={true} suppressContentEditableWarning={true}>DNI :</td>
                            <td className="font-medium pb-1" contentEditable={true} suppressContentEditableWarning={true}>{safeCustomer.taxId || ''}</td>
                        </tr>
                        <tr>
                            <td className="w-24 text-right pr-4 pb-1 align-top text-gray-700" contentEditable={true} suppressContentEditableWarning={true}>Address :</td>
                            <td className="font-medium pb-1" contentEditable={true} suppressContentEditableWarning={true}>{safeCustomer.address || ''}</td>
                        </tr>
                        <tr>
                            <td className="w-24 text-right pr-4 pb-1 align-top text-gray-700" contentEditable={true} suppressContentEditableWarning={true}>Email :</td>
                            <td className="font-medium pb-1" contentEditable={true} suppressContentEditableWarning={true}>{safeCustomer.email || ''}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Items Table */}
            <div className="mb-8">
                <table className="border-collapse border border-gray-300 relative left-1/2 -translate-x-1/2" style={{ width: '120%' }}>
                    <thead>
                        <tr className="text-white font-bold" style={{ backgroundColor: '#475569' }}>
                            <th className="border border-gray-300 p-2 text-left w-3/5" contentEditable={true} suppressContentEditableWarning={true}>Description</th>
                            <th className="border border-gray-300 p-2 text-center w-1/5" contentEditable={true} suppressContentEditableWarning={true}>{invoice?.quantityLabel || 'Quantity'}</th>
                            <th className="border border-gray-300 p-2 text-center w-1/5" contentEditable={true} suppressContentEditableWarning={true}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoice?.items?.length > 0 ? (
                            invoice.items.map((item: any, index: number) => (
                                <tr key={index}>
                                    <td className="border border-gray-300 p-2" contentEditable={true} suppressContentEditableWarning={true}>{item.description}</td>
                                    <td className="border border-gray-300 p-2 text-center" contentEditable={true} suppressContentEditableWarning={true}>{item.quantity}</td>
                                    <td className="border border-gray-300 p-2 text-center font-medium" contentEditable={true} suppressContentEditableWarning={true}>{formatCurrency(item.total)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className="border border-gray-300 p-2 text-center" colSpan={3} contentEditable={true} suppressContentEditableWarning={true}>No items</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Totals Table */}
            <div className="mb-16">
                <table className="border-collapse border border-gray-300 relative left-1/2 -translate-x-1/2" style={{ width: '120%' }}>
                    <tbody>
                        <tr>
                            <td className="border border-gray-300 p-2 font-bold w-1/2" contentEditable={true} suppressContentEditableWarning={true}>
                                Amount in {currency} (Outside the scope of VAT¹)
                            </td>
                            <td className="border border-gray-300 p-2 text-center font-bold bg-white w-1/4" contentEditable={true} suppressContentEditableWarning={true}>
                                Total Net Payable
                            </td>
                            <td className="border border-gray-300 p-2 text-center font-bold bg-white w-1/4" contentEditable={true} suppressContentEditableWarning={true}>
                                {formatCurrency(invoice?.totalAmount)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Signature Area */}
            <div className="flex justify-start mb-12 ml-12 relative h-48">
                <div className="flex gap-4">
                    <span className="text-gray-700" contentEditable={true} suppressContentEditableWarning={true}>Signature :</span>
                    <div className="w-[384px] h-[144px] border-none border-gray-300 relative">
                        {safeProfile.signatureUrl ? (
                            <img src={`${STATIC_URL}${safeProfile.signatureUrl}`} alt="Signature" className="max-w-full max-h-full opacity-80 mix-blend-multiply" />
                        ) : (
                            <div className="text-blue-700 font-bold italic rotate-[-10deg] opacity-75 pt-2 whitespace-nowrap">
                                <p className="text-xl leading-4 uppercase font-extrabold pb-1 border-b-2 border-blue-700 inline-block pr-6">{safeProfile.companyName || safeProfile.profileName || 'AUTO ENTREPRENEUR'}</p>
                                <p className="text-xs pt-1">ICE : {safeProfile.ice || '....................'}</p>
                                <p className="text-xs">Tél : {safeProfile.phone || '....................'}</p>
                                <p className="text-xs pb-1 border-b-2 border-blue-700 w-auto rounded-xl">Email : {safeProfile.email || '....................'}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Semantic Footer - Absolute positioned in preview, handled specifically in print */}
            <footer className="absolute bottom-8 left-8 right-8 pt-4 border-t border-dashed border-gray-400 bg-white" style={{ fontSize: '14px' }}>
                {safeProfile.profileType === 'Entreprise' ? (
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Entreprise :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.companyName || safeProfile.profileName || ''}</span>
                        </div>
                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>ICE :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.ice || ''}</span>
                        </div>

                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Address :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.address || ''}</span>
                        </div>
                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>IF (Tax ID) :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.taxIf || ''}</span>
                        </div>

                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>RC :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.rc || ''}</span>
                        </div>
                        <div className="flex text-white">.</div>

                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Phone :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.phone || ''}</span>
                        </div>
                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Email :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.email || ''}</span>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Auto-Entrepreneur :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.companyName || safeProfile.profileName || ''}</span>
                        </div>
                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>CNIE (ID) :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.cnie || ''}</span>
                        </div>

                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Address :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.address || ''}</span>
                        </div>
                        <div className="flex text-white">.</div>

                        <div className="flex">
                            <span className="font-bold w-88" contentEditable={true} suppressContentEditableWarning={true}>ICE (National Auto-Entrepreneur Register Number) :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.ice || ''}</span>
                        </div>
                        <div className="flex text-white">.</div>

                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>IF (Tax ID) :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.taxIf || ''}</span>
                        </div>
                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Professional Tax N° :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.professionalTaxNo || ''}</span>
                        </div>

                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Phone :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.phone || ''}</span>
                        </div>
                        <div className="flex">
                            <span className="font-bold w-36" contentEditable={true} suppressContentEditableWarning={true}>Email :</span>
                            <span contentEditable={true} suppressContentEditableWarning={true}>{safeProfile.email || ''}</span>
                        </div>
                    </div>
                )}
            </footer>

            <style>{`
                @media print {
                    @page {
                        size: A4;
                        margin: 0;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        box-shadow: none;
                        background: white;
                    }
                    .invoice-container {
                        width: 210mm !important;
                        height: 297mm !important;
                        min-height: 297mm !important;
                        padding: 15mm !important;
                        margin: 0 auto !important;
                        box-shadow: none !important;
                        position: relative !important;
                        box-sizing: border-box !important;

                    }
                    footer {
                        position: fixed !important;
                        bottom: 15mm !important;
                        left: 15mm !important;
                        right: 15mm !important;
                        background: white !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default InvoiceTemplate;
